/**
 * Wave 11.4 — WebGL2 helper for spatial filters.
 *
 * Shared off-screen WebGL2 context + program cache + ping-pong RGBA32F
 * framebuffer pattern used by the Gaussian, Bilateral, and Sobel GPU
 * paths. Designed as an OPTIONAL accelerator: every caller MUST first
 * check `isGPUAvailable()` (single-shot probe + memoised) and fall
 * back to the existing pure-JS CPU path if it returns false.
 *
 * Lifecycle:
 *   • Lazy initialisation on first `getGPUContext()` call.
 *   • Single shared off-screen canvas (OffscreenCanvas when available,
 *     hidden HTMLCanvasElement in window contexts that lack it). The
 *     editor never sees the canvas — it's allocated 2×2 and resized
 *     per-call via the shader uniforms + render-target dimensions.
 *   • Programs compiled lazily on first use, cached for the lifetime
 *     of the context. Three programs total (gaussian / bilateral /
 *     sobel) — each shader source string lives in shaders/*.glsl.js.
 *   • Textures + FBOs allocated per `runShader` call and freed on
 *     return. The two-texture ping-pong pair for separable Gaussian
 *     is allocated by the caller and released in `runShader`'s
 *     finally block. Memory peaks at 2× RGBA32F at full image size
 *     (~67 MB for 4K — well inside browser GPU budgets).
 *
 * Failure modes (all fall back to CPU gracefully, no throw to user):
 *   1. WebGL2 unsupported → isGPUAvailable() = false.
 *   2. EXT_color_buffer_float unsupported AND
 *      EXT_color_buffer_half_float unsupported → false.
 *   3. Shader compile failure → false (cached so we don't retry).
 *   4. Context-lost mid-render → reset the cache + return false on
 *      the next call. `runShader` catches and re-throws GL errors so
 *      caller can revert to CPU.
 *
 * Numerical contract: highp float in fragment shader → IEEE 754
 * single-precision. Mismatch vs CPU Float32 is bounded by the round-
 * tripping order of operations only — the GPU vs CPU parity test
 * accepts RMS < 1e-3 in normalised [0..2] luminance, which is well
 * above the floating-point rounding error we measure in practice.
 *
 * References:
 *   • Khronos OpenGL ES 3.0 spec (WebGL2 is a subset).
 *   • W3C WebGL 2.0 spec, §3.7.5 (Renderbuffer formats), §3.7.16
 *     (Texture object).
 *   • W3C EXT_color_buffer_float extension.
 *
 * License: BLRRD is MIT. No GPLv3 source consulted.
 *
 * @module src/pipeline/filters/spatial/_gpu
 */

import { FRAG_GAUSSIAN_SEPARABLE, KMAX as GAUSS_KMAX }
    from './shaders/gaussian.glsl.js';
import { FRAG_BILATERAL, BILATERAL_KMAX, BILATERAL_KSQ_MAX }
    from './shaders/bilateral.glsl.js';
import { FRAG_SOBEL }
    from './shaders/sobel.glsl.js';

/* Full-screen triangle vertex shader — same trick as the HDR backend.
 * No attribute buffer; gl_VertexID drives a 3-vertex covering
 * triangle that exactly tiles UV=[0..1]². */
const VERTEX_FULLSCREEN = /* glsl */ `#version 300 es
precision highp float;
out vec2 vUv;
void main() {
    vec2 p = vec2(float((gl_VertexID << 1) & 2), float(gl_VertexID & 2));
    vUv = p;
    gl_Position = vec4(p * 2.0 - 1.0, 0.0, 1.0);
}
`;

/** Memoised state. `null` = never probed; `false` = probed unavailable;
 *  object = ready-to-use context. */
let _ctx = null;
let _probed = false;
let _probeResult = false;

/** Test-only force-off flag. When true, `isGPUAvailable()` returns
 *  false regardless of the underlying capability. Production code
 *  never sets this — only the GPU parity test does, to measure the
 *  CPU baseline against the GPU path on the same input. */
let _forceDisabled = false;

/**
 * Diagnostic logger. Guarded so we don't spam the console in
 * production. Opt in via `window.BLRRD_DEBUG_GPU = true`.
 *
 * @param  {...any} args
 */
function debugLog(...args) {
    if (typeof globalThis !== 'undefined' && globalThis.BLRRD_DEBUG_GPU) {
        /* eslint-disable-next-line no-console */
        console.log('[BlrrdFilters.spatial/GPU]', ...args);
    }
}

/**
 * Create the off-screen drawing surface. Window contexts get a 2×2
 * HTMLCanvasElement; Worker contexts use OffscreenCanvas. The actual
 * render-target dimensions are set per call on the FBO-backed
 * texture, NOT on the canvas itself — the canvas is just the WebGL2
 * context owner.
 *
 * @param {number} w
 * @param {number} h
 * @returns {OffscreenCanvas|HTMLCanvasElement}
 */
function createOffscreenSurface(w, h) {
    if (typeof OffscreenCanvas !== 'undefined') {
        return new OffscreenCanvas(w, h);
    }
    if (typeof document !== 'undefined' && document.createElement) {
        const c = document.createElement('canvas');
        c.width = w; c.height = h;
        return c;
    }
    return null;
}

/**
 * Compile a GLSL shader. Returns the compiled WebGLShader or null on
 * failure (logs to debugLog).
 *
 * @param {WebGL2RenderingContext} gl
 * @param {number} kind  gl.VERTEX_SHADER or gl.FRAGMENT_SHADER
 * @param {string} src
 * @returns {WebGLShader|null}
 */
function compileShader(gl, kind, src) {
    const sh = gl.createShader(kind);
    gl.shaderSource(sh, src);
    gl.compileShader(sh);
    if (!gl.getShaderParameter(sh, gl.COMPILE_STATUS)) {
        debugLog('shader compile failed:', gl.getShaderInfoLog(sh));
        gl.deleteShader(sh);
        return null;
    }
    return sh;
}

/**
 * Link a program from vertex + fragment shaders. Returns null on
 * failure (logs to debugLog).
 *
 * @param {WebGL2RenderingContext} gl
 * @param {WebGLShader} vs
 * @param {WebGLShader} fs
 * @returns {WebGLProgram|null}
 */
function linkProgram(gl, vs, fs) {
    const p = gl.createProgram();
    gl.attachShader(p, vs);
    gl.attachShader(p, fs);
    gl.linkProgram(p);
    if (!gl.getProgramParameter(p, gl.LINK_STATUS)) {
        debugLog('program link failed:', gl.getProgramInfoLog(p));
        gl.deleteProgram(p);
        return null;
    }
    return p;
}

/**
 * Collect uniform locations into a flat lookup. Names that aren't
 * declared in the program return null (GL behaviour) — callers must
 * tolerate `null` for unused uniforms.
 *
 * @param {WebGL2RenderingContext} gl
 * @param {WebGLProgram} prog
 * @param {string[]} names
 * @returns {Object<string, WebGLUniformLocation|null>}
 */
function collectUniforms(gl, prog, names) {
    const out = {};
    for (const n of names) out[n] = gl.getUniformLocation(prog, n);
    return out;
}

/**
 * Probe + initialise the shared GPU context. Returns the context
 * object on success, null on any failure. Caches the result so
 * repeated calls are cheap.
 *
 * @returns {{
 *     gl: WebGL2RenderingContext,
 *     canvas: OffscreenCanvas|HTMLCanvasElement,
 *     vao: WebGLVertexArrayObject,
 *     precision: '32f'|'16f',
 *     programs: Object<string, { prog: WebGLProgram, uniforms: Object }>,
 * }|null}
 */
export function getGPUContext() {
    if (_ctx) return _ctx;
    if (_probed && !_probeResult) return null;

    _probed = true;
    try {
        const canvas = createOffscreenSurface(2, 2);
        if (!canvas) {
            debugLog('no canvas runtime available');
            return null;
        }
        const gl = canvas.getContext('webgl2', {
            alpha:               false,
            antialias:           false,
            premultipliedAlpha:  false,
            preserveDrawingBuffer: false,
            powerPreference:     'high-performance',
            desynchronized:      false,
        });
        if (!gl) {
            debugLog('webgl2 unavailable');
            return null;
        }

        /* Float-color-buffer extension probe. RGBA32F first
         * (EXT_color_buffer_float) → RGBA16F fallback
         * (EXT_color_buffer_half_float). Without either we cannot
         * render to a float FBO and the GPU path is unusable. */
        let precision;
        if (gl.getExtension('EXT_color_buffer_float')) {
            precision = '32f';
        } else if (
            gl.getExtension('EXT_color_buffer_half_float') ||
            gl.getExtension('EXT_color_buffer_half_float_2')
        ) {
            precision = '16f';
        } else {
            debugLog('no float color-buffer extension');
            return null;
        }

        /* Compile vertex once — shared across all programs. */
        const vs = compileShader(gl, gl.VERTEX_SHADER, VERTEX_FULLSCREEN);
        if (!vs) return null;

        const sources = {
            gaussian:  FRAG_GAUSSIAN_SEPARABLE,
            bilateral: FRAG_BILATERAL,
            sobel:     FRAG_SOBEL,
        };
        const uniformNames = [
            'uSrc', 'uSrcSize',
            'uWeights[0]', 'uWeights', 'uRadius', 'uAxis',
            'uSpatial[0]', 'uSpatial', 'uInv2SigmaR2',
            'uThreshold',
        ];
        const programs = {};
        for (const [name, src] of Object.entries(sources)) {
            const fs = compileShader(gl, gl.FRAGMENT_SHADER, src);
            if (!fs) {
                debugLog(`compile fail: ${name}`);
                gl.deleteShader(vs);
                return null;
            }
            const prog = linkProgram(gl, vs, fs);
            gl.deleteShader(fs);
            if (!prog) {
                debugLog(`link fail: ${name}`);
                gl.deleteShader(vs);
                return null;
            }
            programs[name] = {
                prog,
                uniforms: collectUniforms(gl, prog, uniformNames),
            };
        }
        gl.deleteShader(vs);

        /* Empty VAO — required by spec even when no attributes used. */
        const vao = gl.createVertexArray();

        _ctx = { gl, canvas, vao, precision, programs };
        _probeResult = true;
        debugLog(`initialised (precision=${precision})`);

        /* Context-lost handler — reset cache so the next call re-probes
         * (which will likely fail again, but we'll know). */
        if (canvas.addEventListener) {
            canvas.addEventListener('webglcontextlost', (ev) => {
                ev.preventDefault();
                debugLog('webglcontextlost — resetting cache');
                _ctx = null;
                _probeResult = false;
                _probed = true;  /* don't retry without a manual reset */
            });
        }
        return _ctx;
    } catch (err) {
        debugLog('init threw', err);
        _probeResult = false;
        return null;
    }
}

/**
 * Public probe — does this runtime support the spatial GPU pipeline?
 * Memoised; first call triggers initialisation. Subsequent calls are
 * O(1). Safe to call from non-GPU code paths (returns false in Node).
 *
 * Honours the `_forceDisabled` test flag (set via `setForceDisabled`).
 *
 * @returns {boolean}
 */
export function isGPUAvailable() {
    if (_forceDisabled) return false;
    return getGPUContext() != null;
}

/**
 * Test hook — force `isGPUAvailable()` to return false (or restore
 * the natural behaviour). Used by the GPU parity test to capture the
 * CPU baseline without modifying any code under test.
 *
 * @param {boolean} forced
 */
export function setForceDisabled(forced) {
    _forceDisabled = !!forced;
}

/**
 * Allocate an RGBA float texture sized w×h. data may be a
 * Float32Array of length 4·w·h to upload, or null for an empty target.
 *
 * @param {WebGL2RenderingContext} gl
 * @param {'32f'|'16f'} precision
 * @param {number} w
 * @param {number} h
 * @param {Float32Array|null} data
 * @returns {WebGLTexture}
 */
function createRGBAFloatTexture(gl, precision, w, h, data) {
    const tex = gl.createTexture();
    gl.bindTexture(gl.TEXTURE_2D, tex);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.NEAREST);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.NEAREST);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
    const internal = (precision === '16f') ? gl.RGBA16F : gl.RGBA32F;
    gl.texImage2D(gl.TEXTURE_2D, 0, internal, w, h, 0, gl.RGBA, gl.FLOAT, data);
    gl.bindTexture(gl.TEXTURE_2D, null);
    return tex;
}

/**
 * Attach a texture as colour-0 to a fresh FBO. Returns the FBO; caller
 * disposes both. Throws on incompleteness so we revert to CPU.
 *
 * @param {WebGL2RenderingContext} gl
 * @param {WebGLTexture} tex
 * @returns {WebGLFramebuffer}
 */
function createFBO(gl, tex) {
    const fbo = gl.createFramebuffer();
    gl.bindFramebuffer(gl.FRAMEBUFFER, fbo);
    gl.framebufferTexture2D(gl.FRAMEBUFFER, gl.COLOR_ATTACHMENT0,
                            gl.TEXTURE_2D, tex, 0);
    const status = gl.checkFramebufferStatus(gl.FRAMEBUFFER);
    if (status !== gl.FRAMEBUFFER_COMPLETE) {
        gl.bindFramebuffer(gl.FRAMEBUFFER, null);
        gl.deleteFramebuffer(fbo);
        throw new Error(`FBO incomplete (status=0x${status.toString(16)})`);
    }
    gl.bindFramebuffer(gl.FRAMEBUFFER, null);
    return fbo;
}

/**
 * Free a texture if not null. Swallows GL errors so we don't crash on
 * cleanup paths.
 *
 * @param {WebGL2RenderingContext} gl
 * @param {WebGLTexture|null} tex
 */
function freeTexture(gl, tex) {
    if (!tex) return;
    try { gl.deleteTexture(tex); } catch (_) { /* ignore */ }
}

/**
 * Free an FBO if not null. Swallows GL errors so we don't crash on
 * cleanup paths.
 *
 * @param {WebGL2RenderingContext} gl
 * @param {WebGLFramebuffer|null} fbo
 */
function freeFBO(gl, fbo) {
    if (!fbo) return;
    try { gl.deleteFramebuffer(fbo); } catch (_) { /* ignore */ }
}

/**
 * Upload a packed RGBA Float32Array to a fresh GPU texture. The
 * returned texture is owned by the caller — pair with `freeTexture`.
 *
 * @param {Float32Array} rgba  packed RGBA, length 4·w·h
 * @param {number} w
 * @param {number} h
 * @returns {{ tex: WebGLTexture, gl: WebGL2RenderingContext }|null}
 *          or null if the GPU context isn't available
 */
export function uploadFloat32(rgba, w, h) {
    const ctx = getGPUContext();
    if (!ctx) return null;
    const tex = createRGBAFloatTexture(ctx.gl, ctx.precision, w, h, rgba);
    return { tex, gl: ctx.gl };
}

/**
 * Download an FBO's colour-0 attachment into a Float32Array of length
 * 4·w·h. Throws on GL error.
 *
 * @param {WebGL2RenderingContext} gl
 * @param {WebGLFramebuffer} fbo
 * @param {number} w
 * @param {number} h
 * @returns {Float32Array}
 */
export function downloadFloat32(gl, fbo, w, h) {
    const out = new Float32Array(w * h * 4);
    gl.bindFramebuffer(gl.FRAMEBUFFER, fbo);
    gl.readPixels(0, 0, w, h, gl.RGBA, gl.FLOAT, out);
    const err = gl.getError();
    gl.bindFramebuffer(gl.FRAMEBUFFER, null);
    if (err !== gl.NO_ERROR) {
        throw new Error(`readPixels failed (gl.getError=0x${err.toString(16)})`);
    }
    return out;
}

/**
 * Internal — execute a single fragment pass.
 * Caller binds program + sets uniforms; this function:
 *
 *   • binds the destination FBO,
 *   • sets the viewport,
 *   • binds the VAO,
 *   • binds the source texture to TEXTURE0 + uniform `uSrc`,
 *   • draws 3 vertices (covers the screen).
 *
 * @param {object} ctx                     return value of getGPUContext()
 * @param {{ prog: WebGLProgram, uniforms: object }} entry
 * @param {WebGLTexture}     srcTex
 * @param {WebGLFramebuffer} dstFBO
 * @param {number} w
 * @param {number} h
 */
export function drawPass(ctx, entry, srcTex, dstFBO, w, h) {
    const { gl, vao } = ctx;
    gl.bindFramebuffer(gl.FRAMEBUFFER, dstFBO);
    gl.viewport(0, 0, w, h);
    gl.useProgram(entry.prog);
    gl.bindVertexArray(vao);
    gl.activeTexture(gl.TEXTURE0);
    gl.bindTexture(gl.TEXTURE_2D, srcTex);
    if (entry.uniforms.uSrc) gl.uniform1i(entry.uniforms.uSrc, 0);
    if (entry.uniforms.uSrcSize) gl.uniform2i(entry.uniforms.uSrcSize, w, h);
    gl.drawArrays(gl.TRIANGLES, 0, 3);
    gl.bindVertexArray(null);
    gl.bindFramebuffer(gl.FRAMEBUFFER, null);
}

/**
 * Allocate a (texture, fbo) pair sized w×h with optional upload data.
 * Used by callers that need ping-pong targets.
 *
 * @param {object} ctx
 * @param {number} w
 * @param {number} h
 * @param {Float32Array|null} data
 * @returns {{ tex: WebGLTexture, fbo: WebGLFramebuffer }}
 */
export function createRT(ctx, w, h, data) {
    const tex = createRGBAFloatTexture(ctx.gl, ctx.precision, w, h, data);
    const fbo = createFBO(ctx.gl, tex);
    return { tex, fbo };
}

/**
 * Dispose a (texture, fbo) pair. Safe to call with null fields.
 *
 * @param {object} ctx
 * @param {{ tex: WebGLTexture|null, fbo: WebGLFramebuffer|null }} rt
 */
export function freeRT(ctx, rt) {
    if (!rt) return;
    freeFBO(ctx.gl, rt.fbo);
    freeTexture(ctx.gl, rt.tex);
}

/**
 * Render a separable Gaussian on `rgba` (Float32 packed RGBA, length
 * 4·w·h). Two passes (horizontal then vertical) into a ping-pong RT.
 * Returns a new Float32Array of the same length; caller is responsible
 * for copying back into the source buffer if in-place semantics are
 * needed.
 *
 * On any GL failure throws — caller should catch + revert to CPU. The
 * caller's `isGPUAvailable()` check should prevent this in the happy
 * path.
 *
 * @param {Float32Array} rgba
 * @param {number} w
 * @param {number} h
 * @param {Float32Array} weights   half-kernel, normalised, length r+1
 * @param {number} r               half-kernel length (matches weights)
 * @returns {Float32Array}
 */
export function gpuGaussian(rgba, w, h, weights, r) {
    const ctx = getGPUContext();
    if (!ctx) throw new Error('gpuGaussian: GPU unavailable');
    if (r < 1 || r > GAUSS_KMAX) {
        throw new Error(`gpuGaussian: r=${r} out of [1, ${GAUSS_KMAX}]`);
    }
    if (weights.length < r + 1) {
        throw new Error(`gpuGaussian: weights len ${weights.length} < r+1 ${r + 1}`);
    }

    /* Upload weights with zero-padding so the uniform array has
     * exactly KMAX+1 entries. */
    const wArr = new Float32Array(GAUSS_KMAX + 1);
    for (let i = 0; i <= r; i++) wArr[i] = weights[i];

    const gl = ctx.gl;
    const entry = ctx.programs.gaussian;

    let srcRT = null, tmpRT = null, dstRT = null;
    try {
        srcRT = createRT(ctx, w, h, rgba);
        tmpRT = createRT(ctx, w, h, null);
        dstRT = createRT(ctx, w, h, null);

        gl.useProgram(entry.prog);
        if (entry.uniforms.uWeights) gl.uniform1fv(entry.uniforms.uWeights, wArr);
        if (entry.uniforms['uWeights[0]'] && !entry.uniforms.uWeights) {
            gl.uniform1fv(entry.uniforms['uWeights[0]'], wArr);
        }
        if (entry.uniforms.uRadius) gl.uniform1i(entry.uniforms.uRadius, r);

        /* Pass 1: horizontal. src → tmp. */
        if (entry.uniforms.uAxis) gl.uniform1i(entry.uniforms.uAxis, 0);
        drawPass(ctx, entry, srcRT.tex, tmpRT.fbo, w, h);

        /* Pass 2: vertical. tmp → dst. */
        if (entry.uniforms.uAxis) gl.uniform1i(entry.uniforms.uAxis, 1);
        drawPass(ctx, entry, tmpRT.tex, dstRT.fbo, w, h);

        const out = downloadFloat32(gl, dstRT.fbo, w, h);
        /* Codex 11.4 STOP-2 parity fix: the CPU separable Gaussian
         * (capture-sharpen.js::applyGaussian1D) leaves alpha
         * untouched, only convolves RGB. The shader convolves vec4
         * for simplicity (no per-channel branches in the inner
         * loop), so we restore the source alpha host-side. Single
         * tight loop, negligible cost on top of the GPU dispatch. */
        for (let i = 3; i < out.length; i += 4) out[i] = rgba[i];
        return out;
    } finally {
        freeRT(ctx, srcRT);
        freeRT(ctx, tmpRT);
        freeRT(ctx, dstRT);
    }
}

/**
 * Render Tomasi-Manduchi bilateral filter on `rgba`. Returns a new
 * Float32Array. Caller passes the pre-computed flat spatial kernel
 * (length (2r+1)²) AND the 1/(2·σ_r²) coefficient — the shader does
 * the range Gaussian per-tap.
 *
 * @param {Float32Array} rgba
 * @param {number} w
 * @param {number} h
 * @param {Float32Array} spatial     length (2r+1)², row-major
 * @param {number} r                 half-window radius, ≤ BILATERAL_KMAX
 * @param {number} inv2SigmaR2       1 / (2 · σ_range²)
 * @returns {Float32Array}
 */
export function gpuBilateral(rgba, w, h, spatial, r, inv2SigmaR2) {
    const ctx = getGPUContext();
    if (!ctx) throw new Error('gpuBilateral: GPU unavailable');
    if (r < 1 || r > BILATERAL_KMAX) {
        throw new Error(`gpuBilateral: r=${r} out of [1, ${BILATERAL_KMAX}]`);
    }
    const expected = (2 * r + 1) * (2 * r + 1);
    if (spatial.length !== expected) {
        throw new Error(`gpuBilateral: spatial len ${spatial.length} != ${expected}`);
    }

    /* Pad spatial up to the compile-time max so the uniform-array
     * upload always has the same length. */
    const sArr = new Float32Array(BILATERAL_KSQ_MAX);
    sArr.set(spatial);

    const gl = ctx.gl;
    const entry = ctx.programs.bilateral;

    let srcRT = null, dstRT = null;
    try {
        srcRT = createRT(ctx, w, h, rgba);
        dstRT = createRT(ctx, w, h, null);

        gl.useProgram(entry.prog);
        if (entry.uniforms.uSpatial) gl.uniform1fv(entry.uniforms.uSpatial, sArr);
        if (entry.uniforms['uSpatial[0]'] && !entry.uniforms.uSpatial) {
            gl.uniform1fv(entry.uniforms['uSpatial[0]'], sArr);
        }
        if (entry.uniforms.uRadius) gl.uniform1i(entry.uniforms.uRadius, r);
        if (entry.uniforms.uInv2SigmaR2) {
            gl.uniform1f(entry.uniforms.uInv2SigmaR2, inv2SigmaR2);
        }
        drawPass(ctx, entry, srcRT.tex, dstRT.fbo, w, h);

        return downloadFloat32(gl, dstRT.fbo, w, h);
    } finally {
        freeRT(ctx, srcRT);
        freeRT(ctx, dstRT);
    }
}

/**
 * Render Sobel 3×3 magnitude on `rgba`. Returns a new Float32Array.
 * Threshold ≥ 0; magnitudes below threshold are clamped to zero.
 *
 * @param {Float32Array} rgba
 * @param {number} w
 * @param {number} h
 * @param {number} threshold
 * @returns {Float32Array}
 */
export function gpuSobel(rgba, w, h, threshold) {
    const ctx = getGPUContext();
    if (!ctx) throw new Error('gpuSobel: GPU unavailable');

    const gl = ctx.gl;
    const entry = ctx.programs.sobel;

    let srcRT = null, dstRT = null;
    try {
        srcRT = createRT(ctx, w, h, rgba);
        dstRT = createRT(ctx, w, h, null);

        gl.useProgram(entry.prog);
        if (entry.uniforms.uThreshold) {
            gl.uniform1f(entry.uniforms.uThreshold, threshold || 0);
        }
        drawPass(ctx, entry, srcRT.tex, dstRT.fbo, w, h);

        return downloadFloat32(gl, dstRT.fbo, w, h);
    } finally {
        freeRT(ctx, srcRT);
        freeRT(ctx, dstRT);
    }
}

/**
 * Test hook — reset memoised state so `isGPUAvailable()` re-probes.
 * Useful in unit tests that swap the WebGL2 implementation. Not part
 * of the production API.
 */
export function _resetForTests() {
    _ctx = null;
    _probed = false;
    _probeResult = false;
    _forceDisabled = false;
}
